"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useState } from "react"

import * as PopoverPrimitive from "@radix-ui/react-popover"

import LoadingSpinner from "@/components/common/LoadingSpinner"
import { Popover, PopoverTrigger } from "@/components/ui/popover"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/components/ui/use-toast"
import { UserAvatar } from "@/components/ui/user-avatar"
import { routes } from "@/constants/routes"
import { useAuth } from "@/core/auth/client/AuthProvider"
import {
  TabletPanel,
  useTabletNotifications
} from "@/features/notifications/TabletPanel"
import type { Notification } from "@/features/v2/notifications/types"
import { cn } from "@/lib/utils"
import {
  CopyIcon,
  ExitIcon,
  HistoryIcon,
  NotificationIcon,
  UserIcon,
  WalletIcon,
  type IconComponent
} from "@/src/shared/assets/icons"
import { resolveAvatarUrl, useAvatarStore } from "@/stores/avatarStore"
import { useAuthStore } from "@/stores/userStore"

import { texts } from "./localization/texts"

const iconClassName =
  "block !size-6 shrink-0 [&_path]:fill-current [&_path]:[fill-opacity:1]"

function IconBox({
  icon: Icon,
  className
}: {
  icon: IconComponent
  className?: string
}) {
  return (
    <span className="inline-flex size-6 shrink-0 items-center justify-center">
      <Icon aria-hidden className={cn(iconClassName, className)} />
    </span>
  )
}

const rowClassName =
  "flex h-9 w-[199px] items-center rounded-[8px] px-2.5 font-sans text-sm font-semibold leading-none tracking-normal text-white/75"

const rowHoverClassName = "hover:bg-black-600 hover:text-white"

type MenuLinkProps = {
  href: string
  icon: IconComponent
  label: string
  className?: string
  trailing?: React.ReactNode
  onNavigate: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

function MenuLink({
  href,
  icon: Icon,
  label,
  className,
  trailing,
  onNavigate
}: MenuLinkProps) {
  return (
    <Link
      href={href}
      prefetch
      onClick={(event) => onNavigate(event)}
      className={cn(
        "flex items-center gap-3 text-base font-medium",
        rowClassName,
        rowHoverClassName,
        className
      )}
    >
      <IconBox icon={Icon} />
      <span className="flex-1 text-left">{label}</span>
      {trailing}
    </Link>
  )
}

type UserMenuProps = {
  avatarUrl: string | null
  unreadCount?: number
  notifications?: Notification[]
}

export function UserMenu({
  avatarUrl,
  unreadCount = 0,
  notifications = []
}: UserMenuProps) {
  const pathname = usePathname()
  const router = useRouter()
  const isTablet = useTabletNotifications()
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  const { account, clearStore } = useAuthStore()

  // Same override the dashboard uploader writes, so changing the avatar there
  // shows up here without waiting for the session cookie cache to expire.
  const avatarState = useAvatarStore()
  const currentAvatar = resolveAvatarUrl(avatarState, avatarUrl)

  const { signOut } = useAuth()
  const { toast } = useToast()

  const badge = unreadCount > 99 ? "99+" : String(unreadCount)

  const closeMenu = () => {
    setIsOpen(false)
  }

  const handleLogout = async () => {
    closeMenu()
    try {
      await signOut()
      clearStore()
      localStorage.removeItem("email")
      localStorage.removeItem("phone")
      localStorage.removeItem("user_id")

      if (pathname.startsWith("/dashboard")) {
        router.push("/")
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Logout failed", error)
    }
  }

  const showSpinner = () => {
    closeMenu()
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 300)
  }

  if (!account) {
    return (
      <div className="relative">
        <Skeleton className="size-11 rounded-[10px] bg-sivarbet-border xl:size-[52px]" />
      </div>
    )
  }

  const handleCopy = () => {
    const id = account.id === undefined ? "" : String(account.id)
    navigator.clipboard.writeText(id).then(() => {
      toast({
        title: texts.copiedTitle,
        description: texts.copiedDescription
      })
    })
  }

  return (
    <div className="relative">
      {isLoading && <LoadingSpinner />}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group relative inline-flex cursor-pointer border-0 bg-transparent p-0"
          >
            <UserAvatar
              src={currentAvatar}
              alt="Avatar"
              className="transition duration-200 group-hover:shadow-[0_0_7px_0_--theme(--color-success-470/28%),0_0_14px_1px_--theme(--color-success-470/13%)]"
            />
            {unreadCount > 0 ? (
              <span
                className="absolute -top-1 -right-1 z-20 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-purple-500 px-1 text-[10px] font-semibold leading-none text-white xlg:hidden"
                aria-label={texts.unreadBadge(unreadCount)}
              >
                {badge}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="end"
            side="bottom"
            sideOffset={8}
            avoidCollisions={false}
            className="z-[60] box-border w-[219px] overflow-hidden rounded-[10px] border border-white/10 bg-black-800 p-2.5 text-white/75 shadow-xl outline-hidden"
          >
            <div className="flex w-full flex-col gap-2">
              <div
                className={cn(
                  "justify-between text-base font-medium",
                  rowClassName
                )}
              >
                <span>
                  {account.id === undefined ? null : texts.id(account.id)}
                </span>
                <button
                  type="button"
                  aria-label={texts.copyId}
                  className="inline-flex size-6 cursor-pointer items-center justify-center text-white/50 hover:text-white"
                  onClick={handleCopy}
                >
                  <CopyIcon aria-hidden className={iconClassName} />
                </button>
              </div>

              <MenuLink
                href={routes.DASHBOARD}
                icon={UserIcon}
                label={texts.profile}
                onNavigate={showSpinner}
              />
              <MenuLink
                href={routes.NOTIFICATIONS}
                icon={NotificationIcon}
                label={texts.notifications}
                className="xlg:hidden"
                onNavigate={(event) => {
                  if (!isTablet) {
                    showSpinner()
                    return
                  }
                  event.preventDefault()
                  closeMenu()
                  setNotificationsOpen(true)
                }}
                trailing={
                  unreadCount > 0 ? (
                    <span
                      className="flex h-6 min-w-6 items-center justify-center rounded-full bg-purple-500 px-1.5 text-xs font-semibold leading-none text-white"
                      aria-label={texts.unreadBadge(unreadCount)}
                    >
                      {badge}
                    </span>
                  ) : null
                }
              />
              <MenuLink
                href={routes.TRANSACTIONS}
                icon={HistoryIcon}
                label={texts.history}
                onNavigate={showSpinner}
              />
              <MenuLink
                href={routes.WITHDRAWALS}
                icon={WalletIcon}
                label={texts.withdrawals}
                onNavigate={showSpinner}
              />
              <button
                type="button"
                onClick={handleLogout}
                className={cn(
                  "group flex cursor-pointer items-center gap-3 text-base font-medium",
                  rowClassName,
                  "text-exit hover:bg-black-600 hover:text-exit"
                )}
              >
                <IconBox
                  icon={ExitIcon}
                  className="text-exit transition-transform duration-200 group-hover:translate-x-1"
                />
                <span>{texts.logout}</span>
              </button>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </Popover>
      <TabletPanel
        open={notificationsOpen}
        notifications={notifications}
        onClose={() => setNotificationsOpen(false)}
      />
    </div>
  )
}
